import { FcOk, FcClock, FcAlarmClock, FcCancel } from "react-icons/fc";
import { Table } from "rsuite";
import { ApiSchemaAppointmentCheck } from "../../models/api";
import { UtilityDate } from "../../utils";
import { useState, useMemo } from 'react';

const { Column, HeaderCell, Cell } = Table;

interface IProps {
  apiData: ApiSchemaAppointmentCheck[];
  isFetching: boolean;
}

export function VisitListTable({
  apiData,
  isFetching,
}: IProps) {
  const [sortBy, setSortBy] = useState<keyof ApiSchemaAppointmentCheck | 'totalTime'>('date');
  const [sortAsc, setSortAsc] = useState(true);

  const calculateTotalTime = (checkInTime?: string, checkOutTime?: string) => {
    if (!checkInTime || !checkOutTime) return '-';
    const checkIn = new Date(checkInTime);
    const checkOut = new Date(checkOutTime);
    const diffMs = checkOut.getTime() - checkIn.getTime();
    const diffHrs = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMins = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    return { hours: diffHrs, minutes: diffMins, text: `${diffHrs}h ${diffMins}m` };
  };

  const sortedData = useMemo(() => {
    if (!apiData) return [];
    return [...apiData].sort((a, b) => {
      if (sortBy === 'patientName') {
        return 0; // Remove sorting by patient name
      }
      if (sortBy === 'date') {
        const aValue = a.date ? new Date(a.date).getTime() : 0;
        const bValue = b.date ? new Date(b.date).getTime() : 0;
        const compareResult = aValue - bValue;
        return sortAsc ? compareResult : -compareResult;
      }
      if (sortBy === 'totalTime') {
        const aTotal = calculateTotalTime(a.checkedInTime, a.checkedOutTime);
        const bTotal = calculateTotalTime(b.checkedInTime, b.checkedOutTime);
        if (typeof aTotal === 'string' || typeof bTotal === 'string') return 0;
        const aValue = aTotal.hours * 60 + aTotal.minutes;
        const bValue = bTotal.hours * 60 + bTotal.minutes;
        const compareResult = aValue - bValue;
        return sortAsc ? compareResult : -compareResult;
      }
      const aValue = a[sortBy];
      const bValue = b[sortBy];
      const compareResult = aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      return sortAsc ? compareResult : -compareResult;
    });
  }, [apiData, sortBy, sortAsc]);

  return (
    <div>
      <Table
        autoHeight
        data={sortedData}
        loading={isFetching}
        className=""
        sortColumn={sortBy}
        sortType={sortAsc ? "asc" : "desc"}
        onSortColumn={(sortColumn, sortType) => {
          setSortBy(sortColumn as keyof ApiSchemaAppointmentCheck | 'totalTime');
          setSortAsc(sortType === "asc");
        }}
        renderEmpty={() => (
          <div className="d-flex justify-content-center text-muted align-items-center h-100 text-center p-5">
            <h3 className="fw-light">No Visit Data Available</h3>
          </div>
        )}
      >
        <Column width={140} sortable>
          <HeaderCell className="fw-bold text-black">Date</HeaderCell>
          <Cell>
            {(rowData: ApiSchemaAppointmentCheck) => (
              <span className="d-flex align-items-center">
                {rowData.checkedInTime ? UtilityDate.formatHumanFriendlyDate(rowData.checkedInTime) : '-'}
              </span>
            )}
          </Cell>
        </Column>

        <Column width={140} sortable>
          <HeaderCell className="fw-bold text-black">Check In Time</HeaderCell>
          <Cell>
            {(rowData: ApiSchemaAppointmentCheck) => (
              <span className="d-flex align-items-center">
                <FcClock className="me-2" />
                {rowData.checkedInTime ? UtilityDate.formatTime(rowData.checkedInTime) : '-'}
              </span>
            )}
          </Cell>
        </Column>

        <Column width={140} sortable>
          <HeaderCell className="fw-bold text-black">Check Out Time</HeaderCell>
          <Cell>
            {(rowData: ApiSchemaAppointmentCheck) => (
              <span className="d-flex align-items-center">
                <FcClock className="me-2" />
                {rowData.checkedOutTime ? UtilityDate.formatTime(rowData.checkedOutTime) : '-'}
              </span>
            )}
          </Cell>
        </Column>

        <Column width={140} sortable>
          <HeaderCell className="fw-bold text-black">Total Time</HeaderCell>
          <Cell>
            {(rowData: ApiSchemaAppointmentCheck) => {
              const totalTime = calculateTotalTime(rowData.checkedInTime, rowData.checkedOutTime);
              return (
                <span className="d-flex align-items-center">
                  <FcAlarmClock className="me-2" />
                  {typeof totalTime === 'string' ? totalTime : totalTime.text}
                </span>
              );
            }}
          </Cell>
        </Column>

        <Column width={140} sortable>
          <HeaderCell className="fw-bold text-black">Verified</HeaderCell>
          <Cell>
            {(rowData: ApiSchemaAppointmentCheck) => (
              <span className="d-flex align-items-center">
                {rowData.isAppointmentVerified ? <FcOk /> : <FcCancel />}
              </span>
            )}
          </Cell>
        </Column>

        <Column width={140} sortable>
          <HeaderCell className="fw-bold text-black">Checked In</HeaderCell>
          <Cell>
            {(rowData: ApiSchemaAppointmentCheck) => (
              <span className="d-flex align-items-center">
                {rowData.isCheckedIn ? <FcOk /> : <FcCancel />}
              </span>
            )}
          </Cell>
        </Column>

        <Column width={140} sortable>
          <HeaderCell className="fw-bold text-black">Checked Out</HeaderCell>
          <Cell>
            {(rowData: ApiSchemaAppointmentCheck) => (
              <span className="d-flex align-items-center">
                {rowData.isCheckedOut ? <FcOk /> : <FcCancel />}
              </span>
            )}
          </Cell>
        </Column>

        <Column width={140} sortable>
          <HeaderCell className="fw-bold text-black">Signature</HeaderCell>
          <Cell>
            {(rowData: ApiSchemaAppointmentCheck) => (
              <span className="d-flex align-items-center">
                {rowData.hasSignature ? <FcOk /> : <FcCancel />}
              </span>
            )}
          </Cell>
        </Column>

        <Column width={140} sortable>
          <HeaderCell className="fw-bold text-black">Auto Clock</HeaderCell>
          <Cell>
            {(rowData: ApiSchemaAppointmentCheck) => (
              <span className="d-flex align-items-center">
                {rowData.AutoClockCheckIn ? <FcOk /> : <FcCancel />}
              </span>
            )}
          </Cell>
        </Column>

        <Column width={140} sortable>
          <HeaderCell className="fw-bold text-black">Max Hours</HeaderCell>
          <Cell>
            {(rowData: ApiSchemaAppointmentCheck) => (
              <span className="d-flex align-items-center">
                {rowData.maxHours}
              </span>
            )}
          </Cell>
        </Column>

      </Table>
    </div>
  );
}